/* ==========================================================================================
   EcoVessel 2026 (forked from the Blenders Online build) · ev-collection.js · the collection page, in place
   sections/ev-main-collection.liquid ([data-ev-coll]) and sections/ev-collection-hero.liquid ([data-ev-chero]).
   No dependencies; deferred; idempotent; re-binds on shopify:section:load.

   [data-ev-coll]                  one products section. Inside it:
     form[data-ev-facets-form]     the filter form (snippets/ev-facets). Ticking, typing a price or moving the
                                   slider asks Shopify's Section Rendering API for this section
                                   (?section_id=...), swaps every [data-ev-swap="key"] part, and keeps a real
                                   address in the bar (pushState; Back and Forward work).
     select[data-ev-sort]          the sort (joined to the form by its form="" attribute)
     select[data-ev-sort-proxy]    the drawer's copy of the sort (phones)
     a[data-ev-coll-link]          chips, Clear all, pages: applied in place ([data-ev-top] scrolls to the grid)
     [data-ev-coll-open] / [data-ev-coll-close] / [data-ev-coll-drawer]   the drawer on phones and tablets
     [data-ev-coll-stick]          the sticky "Filter and sort" button (shown while the grid is on screen and
                                   the bar's own button is not)
     [data-ev-price]               the price slider: two range inputs mirrored into the two number boxes
   [data-ev-chero-desc]            the header's description: the Read more toggle is hidden when the text is
                                   short enough to show whole.
   Any failure falls back to a normal page load of the same address, so filtering never breaks.
   The search builder may put [data-ev-coll] on its own section with the same parts to reuse all of this.
   ========================================================================================== */
(() => {
  'use strict';
  if (window.BOColl) return;
  const reduce = window.matchMedia('(prefers-reduced-motion: reduce)');
  const drawerMq = window.matchMedia('(max-width: 1099px)');
  const KEYS = ['facets', 'clear', 'badge', 'count', 'active', 'done', 'results'];

  /* ---------- the header's description ---------- */
  const descs = (root) => {
    root.querySelectorAll('[data-ev-chero-desc]').forEach((d) => {
      const t = d.querySelector('.ev-chero__text');
      const box = d.querySelector('.ev-chero__toggle');
      if (!t || (box && box.checked)) return;
      const fits = t.scrollHeight <= t.clientHeight + 4;
      d.classList.toggle('is-short', fits);
    });
  };

  /* ---------- the price slider ---------- */
  const priceBind = (root) => {
    root.querySelectorAll('[data-ev-price]').forEach((w) => {
      if (w.boBound) return;
      w.boBound = true;
      const max = parseFloat(w.dataset.max) || 1;
      const rMin = w.querySelector('[data-ev-range="min"]');
      const rMax = w.querySelector('[data-ev-range="max"]');
      const fMin = w.querySelector('[data-ev-field="min"]');
      const fMax = w.querySelector('[data-ev-field="max"]');
      if (!rMin || !rMax || !fMin || !fMax) return;
      const paint = () => {
        const lo = Math.min(+rMin.value, +rMax.value);
        const hi = Math.max(+rMin.value, +rMax.value);
        w.style.setProperty('--lo', (lo / max).toFixed(4));
        w.style.setProperty('--hi', (1 - hi / max).toFixed(4));
      };
      const fromRange = (e) => {
        let a = +rMin.value, b = +rMax.value;
        if (a > b) { if (e.target === rMin) rMin.value = a = b; else rMax.value = b = a; }
        fMin.value = a > 0 ? a : '';
        fMax.value = b < max ? b : '';
        paint();
      };
      const fromField = () => {
        const a = fMin.value === '' ? 0 : Math.max(0, Math.min(max, +fMin.value || 0));
        const b = fMax.value === '' ? max : Math.max(0, Math.min(max, +fMax.value || max));
        rMin.value = Math.min(a, b);
        rMax.value = Math.max(a, b);
        paint();
      };
      rMin.addEventListener('input', fromRange);
      rMax.addEventListener('input', fromRange);
      fMin.addEventListener('input', fromField);
      fMax.addEventListener('input', fromField);
      paint();
    });
  };

  class Coll {
    constructor(el) {
      this.el = el;
      this.id = el.dataset.section;
      this.cache = new Map();
      this.timer = 0;
      this.onChange = this.onChange.bind(this);
      this.onInput = this.onInput.bind(this);
      this.onClick = this.onClick.bind(this);
      this.onKey = this.onKey.bind(this);
      el.addEventListener('change', this.onChange);
      el.addEventListener('input', this.onInput);
      el.addEventListener('click', this.onClick);
      el.addEventListener('submit', (e) => {
        if (e.target.matches('[data-ev-facets-form]')) { e.preventDefault(); this.apply(); }
      });
      this.aside = el.querySelector('[data-ev-coll-drawer]');
      this.scrim = el.querySelector('.ev-coll__scrim');
      this.stickInit();
      priceBind(el);
    }

    get form() { return this.el.querySelector('[data-ev-facets-form]'); }

    onChange(e) {
      const t = e.target;
      if (t.matches('[data-ev-sort-proxy]')) {
        const real = this.el.querySelector('[data-ev-sort]');
        if (real) real.value = t.value;
        return this.apply();
      }
      if (t.matches('[data-ev-sort]')) return this.apply();
      if (t.matches('[data-ev-range]')) return this.apply(150);
      if (t.form && t.form.matches('[data-ev-facets-form]')) {
        if (t.type === 'number') return this.apply(300);
        return this.apply();
      }
      return undefined;
    }

    onInput(e) {
      const t = e.target;
      if (t.type === 'number' && t.form && t.form.matches('[data-ev-facets-form]')) this.apply(900);
    }

    onClick(e) {
      const a = e.target.closest('a[data-ev-coll-link]');
      if (a && this.el.contains(a) && !e.metaKey && !e.ctrlKey && !e.shiftKey && e.button === 0) {
        e.preventDefault();
        this.render(a.href, { top: a.hasAttribute('data-ev-top') });
        return;
      }
      const opener = e.target.closest('[data-ev-coll-open]');
      if (opener) { this.open(opener); return; }
      if (e.target.closest('[data-ev-coll-close]')) this.close();
    }

    onKey(e) {
      if (e.key === 'Escape') { e.preventDefault(); this.close(); return; }
      if (e.key !== 'Tab') return;
      const panel = this.aside && this.aside.querySelector('.ev-coll__panel');
      if (!panel) return;
      const f = [...panel.querySelectorAll('a[href], button:not([disabled]), input:not([disabled]):not([type="hidden"]), select, summary, [tabindex]:not([tabindex="-1"])')]
        .filter((n) => n.offsetParent !== null || n === document.activeElement);
      if (!f.length) return;
      const first = f[0], last = f[f.length - 1];
      if (e.shiftKey && (document.activeElement === first || document.activeElement === panel)) { e.preventDefault(); last.focus(); }
      else if (!e.shiftKey && document.activeElement === last) { e.preventDefault(); first.focus(); }
    }

    /* the address from the form: filters + sort; an empty box and a whole-range price are left out */
    urlFromForm() {
      const params = new URLSearchParams();
      const form = this.form;
      if (form) {
        for (const [k, v] of new FormData(form).entries()) {
          const val = String(v).trim();
          if (val === '') continue;
          if (k.includes('price')) { const n = val.replace(/[^\d.]/g, ''); if (n !== '') params.append(k, n); continue; }
          params.append(k, val);
        }
      }
      const w = this.el.querySelector('[data-ev-price]');
      if (w) {
        const max = parseFloat(w.dataset.max);
        const fMin = w.querySelector('[data-ev-field="min"]');
        const fMax = w.querySelector('[data-ev-field="max"]');
        if (fMin && fMin.value !== '' && +fMin.value <= 0) params.delete(fMin.name);
        if (fMax && fMax.value !== '' && +fMax.value >= max) params.delete(fMax.name);
      }
      const sort = this.el.querySelector('[data-ev-sort]');
      if (sort && sort.dataset.default && params.get('sort_by') === sort.dataset.default) params.delete('sort_by');
      const qs = params.toString();
      return window.location.pathname + (qs ? '?' + qs : '');
    }

    apply(delay = 0) {
      clearTimeout(this.timer);
      this.timer = setTimeout(() => this.render(this.urlFromForm()), delay);
    }

    sectionUrl(href) {
      const u = new URL(href, window.location.origin);
      u.searchParams.delete('section_id');
      u.searchParams.delete('page_view');
      const clean = u.pathname + (u.searchParams.toString() ? '?' + u.searchParams.toString() : '') + u.hash;
      u.searchParams.set('section_id', this.id);
      return { clean, fetchUrl: u.pathname + '?' + u.searchParams.toString() };
    }

    async fetchHTML(url) {
      if (this.cache.has(url)) return this.cache.get(url);
      if (this.ctrl) this.ctrl.abort();
      this.ctrl = new AbortController();
      const res = await fetch(url, { signal: this.ctrl.signal, headers: { 'X-Requested-With': 'XMLHttpRequest' } });
      if (!res.ok) throw new Error('HTTP ' + res.status);
      const html = await res.text();
      if (this.cache.size > 24) this.cache.clear();
      this.cache.set(url, html);
      return html;
    }

    setLoading(on) {
      this.el.classList.toggle('is-loading', on);
      const r = this.el.querySelector('[data-ev-swap="results"]');
      if (r) r.setAttribute('aria-busy', String(on));
    }

    async render(href, opts = {}) {
      const { clean, fetchUrl } = this.sectionUrl(href);
      this.setLoading(true);
      let html;
      try {
        html = await this.fetchHTML(fetchUrl);
      } catch (err) {
        if (err.name === 'AbortError') return;
        window.location.href = clean;
        return;
      }
      const doc = new DOMParser().parseFromString(html, 'text/html');
      if (!doc.querySelector('[data-ev-swap="results"]')) { window.location.href = clean; return; }
      this.swap(doc);
      this.setLoading(false);
      if (opts.push !== false) history.pushState({ boColl: this.id }, '', clean);
      if (this.isOpen()) { this.dirty = true; return; }
      if (opts.top || opts.push !== false) this.toResults(!!opts.top);
    }

    swap(doc) {
      const el = this.el;
      const open = {};
      el.querySelectorAll('details[id]').forEach((d) => { open[d.id] = d.open; });
      const active = document.activeElement;
      let focusSel = '';
      if (active && el.contains(active)) {
        if (active.id) focusSel = '#' + CSS.escape(active.id);
        else if (active.dataset.boField) focusSel = '[data-ev-field="' + active.dataset.boField + '"]';
        else if (active.dataset.boRange) focusSel = '[data-ev-range="' + active.dataset.boRange + '"]';
        else if (active.closest('.ev-coll__active')) focusSel = '.ev-coll__active a, [data-ev-sort]';
      }
      const body = el.querySelector('.ev-coll__panel-body');
      const top = body ? body.scrollTop : 0;
      const results = el.querySelector('[data-ev-swap="results"]');
      if (results && window.BO && BO.release) BO.release(results);

      KEYS.forEach((key) => {
        const now = el.querySelectorAll(`[data-ev-swap="${key}"]`);
        const next = doc.querySelectorAll(`[data-ev-swap="${key}"]`);
        now.forEach((n, i) => { if (next[i]) n.innerHTML = next[i].innerHTML; });
      });
      const sortNext = doc.querySelector('[data-ev-sort]');
      el.querySelectorAll('[data-ev-sort], [data-ev-sort-proxy]').forEach((s) => { if (sortNext) s.value = sortNext.value; });

      el.querySelectorAll('details[id]').forEach((d) => { if (d.id in open) d.open = open[d.id]; });
      if (body) body.scrollTop = top;
      if (focusSel) {
        const f = el.querySelector(focusSel);
        if (f) f.focus({ preventScroll: true });
      }
      priceBind(el);
      if (window.BO && BO.init) BO.init(el.querySelector('[data-ev-swap="results"]') || el);
      if (window.BOFx && BOFx.init) BOFx.init(el);
      this.stickCheck();
    }

    toResults(force) {
      const target = this.el.querySelector('.ev-coll__main');
      if (!target) return;
      const offset = (parseFloat(getComputedStyle(this.el).getPropertyValue('--ev-coll-top')) || 110);
      const y = Math.max(0, target.getBoundingClientRect().top + window.scrollY - offset);
      if (force || window.scrollY > y + 4) window.scrollTo({ top: y, behavior: reduce.matches ? 'auto' : 'smooth' });
    }

    isOpen() { return !!(this.aside && this.aside.classList.contains('is-open')); }

    open(opener) {
      if (!this.aside || !drawerMq.matches) return;
      this.last = opener || null;
      this.aside.classList.add('is-open');
      if (this.scrim) this.scrim.classList.add('is-on');
      document.documentElement.classList.add('ev-coll-locked');
      this.el.querySelectorAll('[data-ev-coll-open]').forEach((b) => b.setAttribute('aria-expanded', 'true'));
      const panel = this.aside.querySelector('.ev-coll__panel');
      if (panel) {
        panel.setAttribute('role', 'dialog');
        panel.setAttribute('aria-modal', 'true');
        const close = panel.querySelector('.ev-coll__close');
        setTimeout(() => (close || panel).focus({ preventScroll: true }), 60);
      }
      document.addEventListener('keydown', this.onKey);
    }

    close() {
      if (!this.isOpen()) return;
      this.aside.classList.remove('is-open');
      if (this.scrim) this.scrim.classList.remove('is-on');
      document.documentElement.classList.remove('ev-coll-locked');
      this.el.querySelectorAll('[data-ev-coll-open]').forEach((b) => b.setAttribute('aria-expanded', 'false'));
      const panel = this.aside.querySelector('.ev-coll__panel');
      if (panel) { panel.setAttribute('role', 'region'); panel.removeAttribute('aria-modal'); }
      document.removeEventListener('keydown', this.onKey);
      if (this.last && this.last.isConnected) this.last.focus({ preventScroll: true });
      if (this.dirty) { this.dirty = false; requestAnimationFrame(() => this.toResults(false)); }
    }

    /* the sticky phone button: while the grid is on screen and the bar's own Filter button is not */
    stickInit() {
      this.stick = this.el.querySelector('[data-ev-coll-stick]');
      if (!this.stick || !('IntersectionObserver' in window)) return;
      this.seenMain = false;
      this.seenBar = true;
      const main = this.el.querySelector('.ev-coll__main');
      const bar = this.el.querySelector('.ev-coll__bar');
      this.io = new IntersectionObserver((ens) => {
        ens.forEach((en) => {
          if (en.target === main) this.seenMain = en.isIntersecting;
          if (en.target === bar) this.seenBar = en.isIntersecting;
        });
        this.stickCheck();
      }, { rootMargin: '0px 0px -120px 0px' });
      if (main) this.io.observe(main);
      if (bar) this.io.observe(bar);
    }

    stickCheck() {
      if (!this.stick) return;
      const grid = this.el.querySelector('[data-ev-coll-grid]');
      this.stick.classList.toggle('is-shown', !!grid && this.seenMain && !this.seenBar && drawerMq.matches);
    }

    destroy() {
      if (this.io) this.io.disconnect();
      this.close();
    }
  }

  const all = new Map();
  const init = (root = document) => {
    descs(root);
    const found = root.matches && root.matches('[data-ev-coll]') ? [root] : [...root.querySelectorAll('[data-ev-coll]')];
    found.forEach((el) => { if (!all.has(el)) all.set(el, new Coll(el)); });
  };

  window.addEventListener('popstate', () => {
    all.forEach((inst, el) => { if (el.isConnected) inst.render(window.location.href, { push: false }); });
  });
  drawerMq.addEventListener('change', () => all.forEach((inst) => { if (!drawerMq.matches) inst.close(); inst.stickCheck(); }));
  let rz = 0;
  window.addEventListener('resize', () => { clearTimeout(rz); rz = setTimeout(() => descs(document), 200); });

  window.BOColl = { init };
  // The clearance hub's sale band (product-collection builder): the sale engine sets <html data-ev-sale> and fires
  // ev:sale:change; when its detail names the event ({ title } or { name }), [data-ev-sale-title] says that name
  // through its data-ev-sale-tpl; otherwise it keeps its own words (data-default).
  document.addEventListener('ev:sale:change', (e) => {
    const d = (e && e.detail) || {};
    const live = document.documentElement.hasAttribute('data-ev-sale');
    const name = live ? String(d.title || d.name || (d.event && (d.event.title || d.event.name)) || '').trim() : '';
    document.querySelectorAll('[data-ev-sale-title]').forEach((el) => {
      el.textContent = name ? (el.getAttribute('data-ev-sale-tpl') || '[event]').replace('[event]', name) : (el.getAttribute('data-default') || el.textContent);
    });
  });
  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', () => init());
  else init();
  document.addEventListener('shopify:section:load', (e) => init(e.target));
  document.addEventListener('shopify:section:unload', (e) => {
    all.forEach((inst, el) => { if (e.target.contains(el)) { inst.destroy(); all.delete(el); } });
  });
})();
